/** Indian number formatting: lakh/crore grouping, not thousands. */

export function inr(value: number | null | undefined, compact = true): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  if (!compact) {
    return `₹${value.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  }
  const abs = Math.abs(value);
  if (abs >= 1e7) return `₹${(value / 1e7).toFixed(2)} Cr`;
  if (abs >= 1e5) return `₹${(value / 1e5).toFixed(2)} L`;
  if (abs >= 1e3) return `₹${(value / 1e3).toFixed(1)}k`;
  return `₹${value.toFixed(0)}`;
}

export function count(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return value.toLocaleString("en-IN");
}

export function pct(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `${value.toFixed(digits)}%`;
}

export function delta(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
}

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export function ago(iso: string | null | undefined): string {
  if (!iso) return "—";
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

/** Trend direction to a semantic colour class. */
export function trendClass(value: number | null | undefined, higherIsBetter = true): string {
  if (value === null || value === undefined) return "text-ink-faint";
  if (Math.abs(value) <= 3) return "text-ink-muted";
  const good = higherIsBetter ? value > 0 : value < 0;
  return good ? "text-good" : "text-bad";
}


/** Axis label for a trend bucket, matching the granularity the API chose.
 *
 *  A daily bucket rendered as "2026-08-01" is 10 characters per tick and 22 of
 *  them collide into a grey smear; a monthly bucket rendered as "01" loses the
 *  year at a financial-year boundary. So the label follows the bucket size:
 *
 *    day   -> 01 Aug
 *    week  -> w/c 01 Aug
 *    month -> Aug 2026
 */
export function bucketLabel(iso: string, granularity?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  if (granularity === "month") {
    return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
  }
  const stem = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  return granularity === "week" ? `w/c ${stem}` : stem;
}
